import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAvailableSlots } from "api/hooks";
import { addMinutes, format, isSameDay } from "date-fns";
import ReactGA from "react-ga4";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { Divider, Picker, Switch, Typography } from "theme";
import { useError } from "utils/hooks";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, CircularProgress, FormControlLabel, Grid2, useTheme } from "@mui/material";

import { AvailableSlot } from "../../../api/hooks/useAvailableSlots/type.ts";
import SubmitButton from "../Submit/SubmitButton.tsx";

import { convertStingToDate } from "./components/ControlledCalendar.tsx";
import ControlledTimeSelectionRadioGroup from "./components/ControlledTimeSelectionRadioGroup.tsx";
import NoAvailableSlots from "./components/NoAvailableSlots.tsx";
import { SelectedTimeFormScheme, selectedTimeScheme } from "./validation.ts";

function filterSlots(availableSlots: AvailableSlot[], hoursToBook: number): AvailableSlot[] {
  const minutesToBook = hoursToBook * 60;
  const expectedIntervalsCount = minutesToBook / 30 - 1;
  const filteredSlots: AvailableSlot[] = [];

  for (let i = 0; i < availableSlots.length; i++) {
    const currentSlot = availableSlots[i];
    const currentSlotDate = new Date(currentSlot.slotDateTime);
    const slotsPickUpUntil = addMinutes(currentSlotDate, minutesToBook);
    const validSlots: AvailableSlot[] = [];

    for (const slot of availableSlots) {
      const slotDate = new Date(slot.slotDateTime);
      if (slotDate < currentSlotDate) {
        continue;
      }

      if (slotDate <= slotsPickUpUntil) {
        validSlots.push(slot);
      } else {
        break;
      }
    }

    if (validSlots.length >= expectedIntervalsCount) {
      filteredSlots.push(currentSlot);
    } else {
      filteredSlots.push({ ...currentSlot, disabled: true });
    }
  }

  return filteredSlots;
}

const TimeSelectionForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<number>(Number(searchParams.get("r")) || 1);
  const [isNightHoursSelected, setIsNightHoursSelected] = useState<boolean>(false);
  const paramDate = convertStingToDate(searchParams.get("d"));
  const isFirstRender = useRef(true);
  const isNightAlreadySet = useRef(false);

  const { slots, isFetching } = useAvailableSlots({
    date: paramDate?.toISOString(),
    hallId: searchParams.get("h"),
  });

  const {
    watch,
    trigger,
    handleSubmit,
    setValue,
    control,
    formState: { isValid, errors },
  } = useForm<SelectedTimeFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(selectedTimeScheme),
    defaultValues: {
      time: "",
    },
  });

  // TODO: add error handling
  useError(errors);

  const onSubmit: SubmitHandler<SelectedTimeFormScheme> = useCallback(async () => {
    if (isValid) {
      ReactGA.event({
        category: "booking_creation",
        action: "time_selected",
        label: searchParams.toString(),
      });
      navigate(`${Routes.BookingCreationEquipmentSelection}?${searchParams.toString()}`);
    }
  }, [isValid, navigate, searchParams]);

  const slotValue = watch("time");

  const daySlots = useMemo(() => slots.filter((el) => !el.nightHours), [slots]);
  const nightSlots = useMemo(() => slots.filter((el) => el.nightHours), [slots]);

  useEffect(() => {
    if (
      nightSlots.some((el) => el.slotValue === searchParams.get("t")) &&
      !isNightHoursSelected &&
      !isNightAlreadySet.current
    ) {
      setIsNightHoursSelected(true);
      isNightAlreadySet.current = true;
    }
  }, [isNightHoursSelected, nightSlots, searchParams]);

  const sortedSlots: AvailableSlot[] = useMemo(() => {
    if (isFetching) return [];

    return isNightHoursSelected ? filterSlots(nightSlots, range) : filterSlots(daySlots, range);
  }, [isFetching, isNightHoursSelected, nightSlots, range, daySlots]);

  useEffect(() => {
    const currentParamDate = convertStingToDate(searchParams.get("d"));

    if (
      currentParamDate &&
      slotValue &&
      !isSameDay(
        currentParamDate,
        new Date(slotValue.includes("Z") ? slotValue.slice(0, -1) : slotValue),
      )
    ) {
      setValue("time", "");
      setSearchParams((prev) => {
        prev.set("t", "");

        return prev;
      });

      return;
    }

    if (slotValue) {
      setSearchParams((prev) => {
        const slotDate = new Date(slotValue);

        const hours = slotDate.getUTCHours();
        const minutes = slotDate.getUTCMinutes();

        const formattedTime = format(new Date(0, 0, 0, hours, minutes), "HH:mm");

        prev.set("t", formattedTime);
        return prev;
      });
    }
  }, [setSearchParams, slotValue, searchParams, setValue]);

  useEffect(() => {
    const slotDate = searchParams.get("t");
    if (isFirstRender.current && slotDate && slots.length > 0) {
      const slotFromUrl = slots.find((el) => el.slotValue === slotDate);
      if (slotFromUrl && !slotFromUrl?.disabled) {
        setValue("time", slotFromUrl.slotDateTime);
        isFirstRender.current = false;
        trigger();
      } else if (!slotFromUrl) {
        navigate(Routes.BookingCreationHallSelection);
      }
    }
  }, [navigate, searchParams, setValue, slots, trigger]);

  useEffect(() => {
    if (!searchParams.has("d")) {
      setSearchParams((prev) => {
        prev.set("d", format(new Date(), "yyyy-MM-dd"));
        prev.set("r", String(range));

        return prev;
      });
    } else {
      setSearchParams((prev) => {
        prev.set("r", String(range));

        return prev;
      });
    }
  }, [range, searchParams, setSearchParams]);

  if (isFetching) {
    return (
      <Grid2 mt={2} container justifyContent="center">
        <CircularProgress color="secondary" />
      </Grid2>
    );
  }

  if (slots.length === 0) {
    return <NoAvailableSlots />;
  }

  return (
    <>
      <Picker onChange={setRange} selectedValue={range} />
      <Divider />
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={isNightHoursSelected}
              sx={{ m: 1 }}
              onChange={() => setIsNightHoursSelected((prevState) => !prevState)}
            />
          }
          label={
            <Typography variant="body1" weight="semiBold">
              Нічні години
            </Typography>
          }
        />
      </Box>
      {isNightHoursSelected && nightSlots.length === 0 ? (
        <Grid2 mt={2.5} container flexDirection="column" alignItems="center" rowGap={1.25}>
          <Typography variant="body1" textTransform="uppercase" color="primary" weight="semiBold">
            На цю ніч немає вільних місць
          </Typography>
        </Grid2>
      ) : (
        <Box
          component="form"
          autoComplete="off"
          id="time-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          mb={theme.spacing(12)}
        >
          <ControlledTimeSelectionRadioGroup
            shouldShowPrice={isNightHoursSelected}
            key={String(isFirstRender.current)}
            name="time"
            options={sortedSlots}
            control={control}
          />
          {isValid && <SubmitButton form="time-form" disabled={!isValid} />}
        </Box>
      )}
    </>
  );
};

export default TimeSelectionForm;
